/* =========================================================================
 * CTA click tracker (Advanced Tracking)
 * -------------------------------------------------------------------------
 * One delegated, PASSIVE click listener for the whole document. It only
 * observes clicks and fires an anonymous analytics beacon — it never calls
 * preventDefault/stopPropagation, never mutates the DOM, and is fully wrapped
 * in try/catch, so it can never interfere with a link, button, or form.
 *
 * Two ways an element is identified, in priority order:
 *   1. Explicit — an element carrying `data-cta` (+ optional `data-cta-label`
 *      / `data-cta-section`). Gives a clean, curated id. Preferred for the
 *      CTAs you care most about:
 *        <a href="/collections/all"
 *           data-cta="home-hero-shop-now"
 *           data-cta-label="Shop now"
 *           data-cta-section="hero">
 *   2. Auto — any other clickable element (a[href], button, [role=button],
 *      submit/button/image inputs, [onclick]) gets a STABLE generated id so
 *      "most things people can click" are covered without tagging each one.
 *      Auto ids are prefixed so they're easy to spot and later promote:
 *        link:/products/ofen-2   (from the link destination)
 *        id:calc-earnings-buy    (from a stable element id)
 *        text:join the waitlist  (from the button text)
 *      Section is read from the nearest `data-cta-section`, else derived from
 *      the enclosing `shopify-section-*` wrapper.
 *
 * Anonymous: no session/customer identifiers are sent. Gated behind the
 * Shopify Customer Privacy API (analytics consent); fails closed.
 * ========================================================================= */
(function () {
  'use strict';

  var TRACK_URL = 'https://toolbox-api.21energy.io/api/cta/event';
  var MAX_ID_LEN = 120;
  var MAX_LABEL_LEN = 200;

  // Elements we treat as "clickable" for auto-capture.
  var CLICKABLE =
    'a[href], button, [role="button"], input[type="submit"], input[type="button"], input[type="image"], [data-cta], [onclick]';

  // Only track if the visitor has granted analytics consent. Fails closed.
  function analyticsAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') {
        return cp.analyticsProcessingAllowed() === true;
      }
    } catch (e) { /* no-op */ }
    return false;
  }

  function sendEvent(payload) {
    var data = JSON.stringify(payload);
    try {
      if (navigator.sendBeacon) {
        // text/plain is CORS-safelisted (no preflight); the API json-parses regardless.
        navigator.sendBeacon(TRACK_URL, new Blob([data], { type: 'text/plain;charset=UTF-8' }));
        return;
      }
    } catch (e) { /* fall through to fetch */ }
    try {
      fetch(TRACK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: data,
        keepalive: true,
      }).catch(function () {});
    } catch (e) { /* no-op */ }
  }

  function cleanText(s) {
    return (s || '').replace(/\s+/g, ' ').trim();
  }

  // Human-readable label for an element.
  function labelFor(el) {
    var explicit = el.getAttribute('data-cta-label');
    if (explicit) return cleanText(explicit);
    if (el.tagName === 'INPUT') {
      return cleanText(el.value || el.getAttribute('aria-label') || el.getAttribute('title') || '');
    }
    var text = cleanText(el.textContent);
    if (text) return text;
    return cleanText(el.getAttribute('aria-label') || el.getAttribute('title') || '');
  }

  // Section: explicit data-cta-section, else derived from the shopify-section wrapper.
  function sectionFor(el) {
    try {
      var tagged = el.closest('[data-cta-section]');
      if (tagged) return cleanText(tagged.getAttribute('data-cta-section')) || null;
      var sec = el.closest('[id^="shopify-section-"]');
      if (sec && sec.id) {
        // Strip the "template--123456789__" / "123456789__" prefixes Shopify adds.
        return sec.id
          .replace('shopify-section-', '')
          .replace(/^template--\d+__/, '')
          .replace(/^\d+__/, '') || null;
      }
    } catch (e) { /* no-op */ }
    return null;
  }

  // Stable identifier for an element. Explicit data-cta wins; else auto-derive.
  function idFor(el) {
    var explicit = cleanText(el.getAttribute('data-cta'));
    if (explicit) return explicit;

    // A stable element id (skip ids that look auto-generated / hashy).
    var elId = el.getAttribute('id');
    if (elId && !/\d{4,}/.test(elId)) return 'id:' + elId;

    // Links: key on the destination path — stable and meaningful.
    if (el.tagName === 'A') {
      var href = el.getAttribute('href') || '';
      var lower = href.slice(0, 11).toLowerCase();
      if (href && href.charAt(0) !== '#' && lower !== 'javascript:') {
        var dest = href;
        try {
          var u = new URL(href, window.location.origin);
          dest = (u.protocol === 'http:' || u.protocol === 'https:') ? u.pathname : href;
        } catch (e) { /* keep raw href */ }
        return 'link:' + dest;
      }
    }

    // Fallback: the visible text.
    var text = labelFor(el).toLowerCase();
    if (text) return 'text:' + text;

    return null; // can't identify -> skip (don't send junk)
  }

  function onClick(e) {
    try {
      var target = e.target;
      if (!target || !target.closest) return;

      var el = target.closest(CLICKABLE);
      if (!el) return;

      if (!analyticsAllowed()) return;

      var ctaId = idFor(el);
      if (!ctaId) return;

      var label = labelFor(el);

      sendEvent({
        shop: window._21eShopDomain || window.location.hostname,
        ctaId: ctaId.slice(0, MAX_ID_LEN),
        label: label ? label.slice(0, MAX_LABEL_LEN) : undefined,
        section: sectionFor(el) || undefined,
        page: window.location.pathname,
        locale: cleanText(document.documentElement.lang) || undefined,
      });
    } catch (err) {
      // Never let tracking interfere with the click.
    }
  }

  // Capture phase so the beacon fires even when the click navigates away.
  document.addEventListener('click', onClick, true);
})();
